/**
 * MobileEmployeeCard Component
 *
 * Card-based display for employee report data on mobile devices.
 */

import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { colors, typography as typographyTokens, radii, shadows } from '@components/design-system';
import { AppRoutes } from '@consts/routes';
import type { EmployeeResponse } from '@api/org';

interface MobileEmployeeCardProps {
    data: EmployeeResponse;
    dateRange: { startDate: string; endDate: string };
    companyName?: string;
    resellerName?: string;
    locationName?: string;
}

const InfoRow: React.FC<{ label: string; value: React.ReactNode; highlight?: boolean }> = ({
    label,
    value,
    highlight,
}) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
        <Typography
            sx={{
                fontSize: typographyTokens.fontSize.xs,
                color: colors.neutral[500],
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
            }}>
            {label}
        </Typography>
        <Typography
            sx={{
                fontSize: typographyTokens.fontSize.sm,
                color: highlight ? colors.warning.dark : colors.neutral[700],
                fontWeight: highlight ? typographyTokens.fontWeight.semibold : typographyTokens.fontWeight.medium,
                textAlign: 'right',
                maxWidth: '60%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}>
            {value || '-'}
        </Typography>
    </Box>
);

export const MobileEmployeeCard: React.FC<MobileEmployeeCardProps> = ({
    data,
    dateRange,
    companyName,
    resellerName,
    locationName,
}) => {
    const detailsUrl = `${AppRoutes.EmployeeDetails}?startDate=${encodeURIComponent(
        dateRange.startDate
    )}&endDate=${encodeURIComponent(dateRange.endDate)}&email=${encodeURIComponent(
        data.Email
    )}&locationId=${encodeURIComponent(data.LocationID)}`;

    return (
        <Box
            component={RouterLink}
            to={detailsUrl}
            sx={{
                display: 'block',
                textDecoration: 'none',
                backgroundColor: colors.neutral[0],
                borderRadius: `${radii.lg}px`,
                border: `1px solid ${colors.neutral[200]}`,
                p: 2,
                mb: 1.5,
                boxShadow: shadows.card,
                transition: 'all 150ms ease',
                '&:hover': {
                    boxShadow: shadows.elevated,
                    borderColor: colors.primary[200],
                },
                '&:active': {
                    transform: 'scale(0.99)',
                },
            }}>
            {/* Header with icon and employee info */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: `${radii.md}px`,
                        backgroundColor: `${colors.primary[500]}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                    <PersonOutlineOutlinedIcon sx={{ color: colors.primary[500], fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.md,
                                fontWeight: typographyTokens.fontWeight.semibold,
                                color: colors.neutral[900],
                                lineHeight: 1.3,
                            }}>
                            {data.Name}
                        </Typography>
                        <Box
                            sx={{
                                backgroundColor: colors.primary[50],
                                color: colors.primary[700],
                                px: 0.75,
                                py: 0.125,
                                borderRadius: `${radii.full}px`,
                                fontSize: typographyTokens.fontSize.xs,
                                fontWeight: typographyTokens.fontWeight.medium,
                            }}>
                            #{data.EmployeeRank}
                        </Box>
                    </Box>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.xs,
                            color: colors.neutral[500],
                            mt: 0.25,
                        }}>
                        {data.Email}
                    </Typography>
                </Box>
                {/* Stats on the right */}
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.xl,
                            fontWeight: typographyTokens.fontWeight.bold,
                            color: colors.primary[600],
                            lineHeight: 1.2,
                        }}>
                        {data.TotalCuts}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.xs,
                            color: colors.neutral[500],
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        }}>
                        Cuts
                    </Typography>
                </Box>
            </Box>

            {/* Organization & Stats info */}
            <Box sx={{ borderTop: `1px solid ${colors.neutral[100]}`, pt: 1 }}>
                <InfoRow label='Location' value={locationName || 'Unknown'} />
                <InfoRow label='Reseller' value={resellerName || 'None'} />
                <InfoRow label='Company' value={companyName || 'Unknown'} />
                <InfoRow label='Warranty' value={`${data.WarrantyClaimPercentage}%`} highlight />
            </Box>
        </Box>
    );
};

// Loading skeleton for mobile cards
export const MobileEmployeeCardSkeleton: React.FC = () => (
    <Box
        sx={{
            backgroundColor: colors.neutral[0],
            borderRadius: `${radii.lg}px`,
            border: `1px solid ${colors.neutral[200]}`,
            p: 2,
            mb: 1.5,
        }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
            <Skeleton variant='rounded' width={40} height={40} />
            <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant='text' width='50%' height={24} />
                    <Skeleton variant='rounded' width={30} height={18} />
                </Box>
                <Skeleton variant='text' width='70%' height={16} />
            </Box>
            <Box sx={{ textAlign: 'right' }}>
                <Skeleton variant='text' width={40} height={28} />
                <Skeleton variant='text' width={30} height={14} />
            </Box>
        </Box>
        <Box sx={{ borderTop: `1px solid ${colors.neutral[100]}`, pt: 1 }}>
            <Skeleton variant='text' width='100%' height={20} />
            <Skeleton variant='text' width='100%' height={20} />
            <Skeleton variant='text' width='100%' height={20} />
            <Skeleton variant='text' width='100%' height={20} />
        </Box>
    </Box>
);

export default MobileEmployeeCard;
